import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useLoader } from "../../context/LoaderContext";
import { ColDef } from "ag-grid-enterprise";
import { useNavigate } from "react-router-dom";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import SearchIcon from "@mui/icons-material/Search";
import { Tooltip, IconButton, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import CloseIcon from "@mui/icons-material/Close";
import { getExcelData } from "../../api/groupsApi/groupsApi";
import { getMetadataDefinition } from "../../api/metadataDefinitionApi/metadataDefinitionApi";
import { useSnackbar } from "../Common/SnackbarContext";
import { Col, Row } from "react-bootstrap";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";
import { appSettings } from "../../config/appSetting";
import moment from "moment";
interface MetadataDefinitionMangementProps {
  businessUnitData: any;
}

const Actions = ({
  data,
  navigation,
}: {
  data: any;
  navigation: any;
}) => {
  if (data === undefined || data === null) {
    return null;
  }
  const handleEditAction = () => {
    const selectedRowData = data;
    navigation("/metadatadefinition", {
      state: { rowData: selectedRowData, action: "Edit" },
    });
  };
  return (
    <>
      {isPagePermission("Allow Edit", "MetaDataDefinition", 733) && (
        <Tooltip title="Click to edit the Definition">
          <EditIcon
            className="icon-button-other"
            onClick={handleEditAction}
            style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
          />
        </Tooltip>
      )}
    </>
  );
};


const MetadataDefinitionManagement: React.FC<
  MetadataDefinitionMangementProps
> = ({ businessUnitData }) => {
  const { setLoading } = useLoader();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [, setRecordsFound] = useState(0);// NOSONAR
  const pageSizeSelector = [5, 10, 25, 50, 100, 500];
  const defaulPageSize = 25;
  const [rowData, setRowData] = useState<any[]>([]);
  const { showSnackbar } = useSnackbar();
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleConfirmDialogClose = (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed) {
      console.log("Cancel confirmed");
    }
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "	Definition Name",
      field: "metadataDefinitionName",
      tooltipField: "metadataDefinitionName",
      width: 250,
      colId: "metadataDefinitionName",
      flex: 1,
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Selected Metadata Fields",
      field: "metadatadefinitionGrid",
      tooltipField: "metadatadefinitionGrid",
      width: 250,
      colId: "metadatadefinitionGrid",
      flex: 1,
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Created On",
      field: "createdDate",
      tooltipField: "createdDate",
      width: 270,
      flex: 1,
      colId: "createdDate",
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
      filter: true,
    },
    {
      headerName: "Updated By",
      field: "updatedBy",
      tooltipField: "updatedBy",
      width: 270,
      flex: 1,
      colId: "updatedBy",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: (params: any) => (
        <Actions data={params.data} navigation={navigation} />
      ),
      lockPinned: true,
      suppressSizeToFit: true,
      width: 100,
      minWidth: 100,
      pinned: "right",
    },
  ]);

  const getData = async (metadata: any) => {
    setLoading(true);
    try {
      const response = await getMetadataDefinition(metadata);
      if (metadata && searchTerm) {
        showSnackbar(response.totalCount + " records found.");
      }
      setRowData(response.metaDataDefinitiongrid);
      setRecordsFound(response.metaDataDefinitiongrid.length);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(metadataDefinitionModel);
  }, []);

  const params: any = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: searchTerm,
    aliasname: "MetadataDefinition",
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    isexporttpExcel: true,
    offset: appSettings.timezone,
    pagename: "MetadataDefinition",
    sortColumn: "Createddate",
    sortOrder: 1,
  };

  const excelData = async () => {
    try {
      const excelDataResponse = await getExcelData(params);
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      showSnackbar("File downloaded successfully!");
      const date = new Date();
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date
          .getDate()
          .toString()
          .padStart(2, "0")}-${date.getFullYear()}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `MetadataDefinition Reports_${formattedDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching Excel data", error);
    }
  };

  const handelCreateNew = () => {
    navigation("/metadatadefinition");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      console.log("searchTerm", value);
    }
  };
  let metadataDefinitionModel: any = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: "",
    UserId: LocalStorageUtil.getItem("userId"),
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    currentPage: 1,
    isexporttpExcel: false,
    noOfRecords: 25,
    pagename: "MetadataDefinition",
    sortOrder: 0,
  };
  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let metadata: any = {
      ...metadataDefinitionModel,
      SearchText: searchTerm,
    };
    await getData(metadata);
    setShowEmptySearchMessage(false);
  };

  const handleCloseSearch = async () => {
    setSearchTerm("");
    setShowEmptySearchMessage(true);
    await getData(metadataDefinitionModel);
  };
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  return (
    <>
      <NavigationBar menuTitle="Configuration"
        title="Metadata Management"
        screenTitle="Metadata Definition" text="Metadata Definition" />
      <Row style={{ margin: "1em" }}>
        <Col>
          <div className="searchContainer">
            <div className="searchWrapper">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  overflow: "hidden",
                  height: "1.6em",
                }}
              >
                <input
                  type="text"
                  placeholder="Search By Definition Names"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyPress}
                  className="searchInput"
                  data-testid="search-input"
                  style={{
                    width: "25em",
                    height: "2.5em",
                    fontSize: "11px",
                    paddingLeft: ".5em",
                    border: "none",
                  }}
                />
                <Tooltip title="Click to search">
                  <button onClick={handleSearchClick} className="searchButton">
                    <SearchIcon style={{ color: "#006ab6" }} />
                  </button>
                </Tooltip>
                {searchTerm && (
                  <Tooltip title="Click to reset search result">
                    <div>
                      <IconButton
                        onClick={handleCloseSearch}
                        style={{ padding: "5px" }}
                        data-testid="clear-search"
                      >
                        <CloseIcon
                          style={{ color: "var(--aggrid-text-color)" }}
                        />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
              </div>
              {showEmptySearchMessage && (
                <span className="errorMessage" style={{ marginLeft: "20px" }}>
                  Please enter text for search
                </span>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "end" }}>
              {isPagePermission("Allow Create New", "MetaDataDefinition", 731) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button create-new-button"
                  onClick={handelCreateNew}
                >
                  Create New
                </Button>
              )}
              {isPagePermission("Allow Export to Excel", "MetaDataDefinition", 732) && (
                <Tooltip title="Click to export the grid content in excel">
                  <button
                    type="button"
                    onClick={excelData}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    aria-label="Export to Excel"
                  >
                    <img
                      src={ExcelIcon}
                      alt="Excel Icon"
                      className="metadata-defination-excel-icon"
                    />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="rp-main-inner col-sm-12">
            <div className="rp-main-inner col-sm-12">
              <div
                className="ag-theme-quartz-dark"
                style={{
                  height: "66vh",
                }}
              >
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={rowData}
                  pagination={true}
                  paginationPageSize={defaulPageSize}
                  paginationPageSizeSelector={pageSizeSelector}
                  rowGroupPanelShow="always"
                  pivotPanelShow="always"
                  ref={gridRef}
                  headerHeight={30}
                  gridOptions={{
                    rowSelection: {
                      mode: "multiRow",
                      checkboxes: true,
                      headerCheckbox: true,
                      enableClickSelection: false,
                    },
                  }}
                  overlayLoadingTemplate={
                    '<span class="ag-overlay-loading-center">Loading...</span>'
                  }
                  overlayNoRowsTemplate={
                    '<span class="ag-overlay-loading-center">No rows to show</span>'
                  }
                  loadingOverlayComponentParams={{
                    loadingMessage: "Loading...",
                  }}
                />
              </div>
            </div>
          </div>
          <ConfirmDialog
            open={confirmDialogOpen}
            onClose={handleConfirmDialogClose}
            title="Metadata Group - Update Metadata Group"
            content="Please note that the Metadata-Group is mapped with the Metadata-Field. Are you sure you want to proceed?"
          />
        </Col>
      </Row>
    </>
  );
};

export default MetadataDefinitionManagement;
